/** Теги, содержимое которых стоит на своей строке — за ними при чтении текста граница строки */
const BLOCK_LEVEL_TAGS = new Set([
  'P',
  'DIV',
  'UL',
  'OL',
  'LI',
  'TABLE',
  'TR',
  'BLOCKQUOTE',
  'PRE',
  'H1',
  'H2',
  'H3',
  'H4',
  'H5',
  'H6',
]);

const isBlockLevelElement = (node: Node | null): boolean =>
  node?.nodeType === Node.ELEMENT_NODE && BLOCK_LEVEL_TAGS.has((node as Element).tagName);

/** Соседство, рядом с которым пробельный пропуск уже ничего не разделяет: строка и так кончилась */
const isLineBreakingNeighbour = (node: Node | null): boolean =>
  isBlockLevelElement(node) || (node?.nodeType === Node.ELEMENT_NODE && (node as Element).tagName === 'BR');

/** Разбирает разметку в документ — общий первый шаг для всех переводов её в текст. */
const parseRichText = (html: string): HTMLElement =>
  new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html').body;

/**
 * Теги, содержимое которых — одна строка текста. Пустыми они значат строку, оставленную пустой,
 * тогда как пустой `ul` или `li`-контейнер — лишь остаток разметки.
 */
const TEXT_LINE_TAGS = new Set(['P', 'DIV', 'LI']);

/**
 * Строка, оставленная в тексте пустой: в разметке она записана блоком без содержимого.
 *
 * Редактор держит такую строку переносом внутри блока, но в сохранённый текст перенос не попадает —
 * блок остаётся пустым, и на экране высоты не занимает, если ему не вернуть перенос.
 */
export const isBlankRichTextLine = (element: Element): boolean =>
  TEXT_LINE_TAGS.has(element.tagName) && element.childElementCount === 0 && !element.textContent?.trim();

/**
 * Соседний узел в порядке чтения. Выделение — отрезок строки, на которой стоит, а не своя строка:
 * у его края текст продолжается тем, что стоит рядом с выделением (`и<i> конец</i>` — пробел между
 * «и» и «конец»). `null` — строка здесь кончается: край блока или всей разметки.
 */
const getReadingNeighbour = (node: Node, isForward: boolean): Node | null => {
  const sibling = isForward ? node.nextSibling : node.previousSibling;
  if (sibling) return sibling;

  const parent = node.parentElement;
  if (!parent || isBlockLevelElement(parent)) return null;
  return getReadingNeighbour(parent, isForward);
};

/**
 * Пропуск, набранный отступами самой разметки, а не написанный в тексте.
 *
 * Границы строк в тексте заданы переносами, и различить их с отступами между тегами можно только
 * по соседству: пропуск на краю содержимого или рядом с началом новой строки ничего не разделяет,
 * а тот же перенос между словами и начертаниями — граница строки, написанная автором.
 * Край выделения краем строки не считается ({@link getReadingNeighbour}): выделенный пробел между
 * словами (`a<b> </b>b`) их разделяет.
 */
export const isRichTextLayoutWhitespace = (node: Text): boolean => {
  if ((node.nodeValue ?? '').trim()) return false;

  const previous = getReadingNeighbour(node, false);
  const next = getReadingNeighbour(node, true);
  if (!previous || !next) return true;

  return isLineBreakingNeighbour(previous) || isLineBreakingNeighbour(next);
};

/** Край строки: дальше в эту сторону строка не продолжается ({@link getReadingNeighbour}) */
const isLineEdge = (neighbour: Node | null): boolean => !neighbour || isLineBreakingNeighbour(neighbour);

/** Пропуск с переносом в начале текста — до последнего его переноса */
const LEADING_LINE_BREAK_PATTERN = /^\s*\n/;

/** Пропуск с переносом в конце текста — от первого его переноса */
const TRAILING_LINE_BREAK_PATTERN = /\n\s*$/;

/**
 * Часть текста узла, которую читает строка: переносы на краю строки в неё не входят.
 *
 * В начале и в конце блока, рядом с переносом разметки и на краю всего содержимого перенос лишь
 * отделяет теги друг от друга (`<p>⏎текст⏎</p>`) — это отступ разметки, как и пробельный узел
 * ({@link isRichTextLayoutWhitespace}). Между словами и выделениями тот же перенос — граница строки,
 * написанная автором. `start` и `end` — позиции в тексте узла.
 */
export const getRichTextLineRange = (node: Text): { start: number; end: number } => {
  const value = node.nodeValue ?? '';
  const start = isLineEdge(getReadingNeighbour(node, false))
    ? (value.match(LEADING_LINE_BREAK_PATTERN)?.[0].length ?? 0)
    : 0;
  const trailingBreak = isLineEdge(getReadingNeighbour(node, true)) ? value.search(TRAILING_LINE_BREAK_PATTERN) : -1;
  const end = trailingBreak === -1 ? value.length : trailingBreak;
  return { start, end: Math.max(start, end) };
};

/**
 * Текст без разметки в одну строку: границы абзацев не сохраняются, соседние абзацы слипаются.
 * Позиции в нём совпадают с отсчётом текста, по которому рисуется разметка.
 */
export const richTextToPlain = (html: string): string => parseRichText(html).textContent ?? '';

/**
 * Текст разметки для сравнения версий: текст без разметки, в котором границы строк, заданные
 * самой разметкой, отмечены вставленными переносами.
 */
export type ComparableRichText = {
  /** Текст без разметки со вставленными переносами */
  text: string;
  /** Позиции вставленных переносов в `text`, по возрастанию */
  insertedBreaks: number[];
};

/**
 * Разметка в текст для сравнения версий.
 *
 * В {@link richTextToPlain} последнее слово абзаца слипается с первым словом следующего, и
 * сравнение считает их одним словом: дописанный абзац выглядит заменой конца прежнего текста.
 * Здесь на границе строк, не разделённой пробелом, стоит перенос. Он не занимает места в тексте, по
 * которому рисуется разметка, поэтому его позиция возвращается отдельно — по ней результат
 * сравнения переводится обратно в отсчёт {@link richTextToPlain}.
 */
export const richTextToComparable = (html: string): ComparableRichText => {
  const comparable: ComparableRichText = { text: '', insertedBreaks: [] };
  let hasPendingBreak = false;

  const appendNode = (node: Node): void => {
    if (node.nodeType === Node.TEXT_NODE) {
      const value = node.nodeValue ?? '';
      if (!value) return;
      if (hasPendingBreak && /\S$/.test(comparable.text) && /^\S/.test(value)) {
        comparable.insertedBreaks.push(comparable.text.length);
        comparable.text += '\n';
      }
      hasPendingBreak = false;
      comparable.text += value;
      return;
    }

    if (node.nodeType !== Node.ELEMENT_NODE) return;

    const isLineBoundary = isLineBreakingNeighbour(node);
    if (isLineBoundary) hasPendingBreak = true;
    for (const child of node.childNodes) appendNode(child);
    if (isLineBoundary) hasPendingBreak = true;
  };

  for (const node of parseRichText(html).childNodes) appendNode(node);
  return comparable;
};

/** Накопитель текста: границы строк ставятся с оглядкой на уже набранное */
type ReadableTextBuilder = {
  text: string;
  /**
   * Разметка, из которой при чтении снимаются переносы, которых текст для чтения не держит
   * ({@link isAtExcessLineBreak}); без неё текст только читается.
   */
  collapsedRoot?: Element;
};

/** Строка уже кончилась: за последним переносом разве что пропуски, которых в конце строки не видно */
const LINE_END_PATTERN = /\n[^\S\n]*$/;

/** Текст кончается пустой строкой: пропуски между переносами пустой строку не делают */
const EMPTY_LINE_END_PATTERN = /\n[^\S\n]*\n[^\S\n]*$/;

/**
 * Перенос встал бы третьим подряд: больше одной пустой строки подряд текст для чтения не держит
 * ({@link richTextToReadableText}), а экран показал бы все — из разметки такой перенос снимается.
 */
const isAtExcessLineBreak = (builder: ReadableTextBuilder): boolean =>
  Boolean(builder.collapsedRoot) && EMPTY_LINE_END_PATTERN.test(builder.text);

/**
 * Снимает узел, а с ним и блок, оставшийся без него пустым: пустой блок на экране — строка
 * ({@link isBlankRichTextLine}), тогда как снятый перенос места не занимает.
 */
const removeWithBlankParents = (node: ChildNode, root: Element): void => {
  const parent = node.parentElement;
  node.remove();
  if (parent && parent !== root && isBlankRichTextLine(parent)) removeWithBlankParents(parent, root);
};

/** Перенос, заданный узлом разметки: лишний ({@link isAtExcessLineBreak}) снимается вместе с узлом */
const appendNodeLineBreak = (node: ChildNode, builder: ReadableTextBuilder): void => {
  const { collapsedRoot } = builder;
  if (collapsedRoot && isAtExcessLineBreak(builder)) {
    removeWithBlankParents(node, collapsedRoot);
    return;
  }
  builder.text += '\n';
};

/**
 * Граница строки, заданная разметкой. Подряд идущие границы схлопываются: соседние абзацы
 * стоят на своих строках, но пустой строкой не разделяются — как и на экране.
 */
const appendBlockBoundary = (builder: ReadableTextBuilder): void => {
  if (!builder.text || LINE_END_PATTERN.test(builder.text)) return;
  builder.text += '\n';
};

const appendReadableNode = (node: ChildNode, builder: ReadableTextBuilder): void => {
  if (node.nodeType === Node.TEXT_NODE) {
    if (isRichTextLayoutWhitespace(node as Text)) return;

    const start = builder.text.length;
    const lineRange = getRichTextLineRange(node as Text);
    const [firstLine, ...nextLines] = (node.nodeValue ?? '').slice(lineRange.start, lineRange.end).split('\n');
    builder.text += firstLine;
    for (const line of nextLines) builder.text += `${isAtExcessLineBreak(builder) ? '' : '\n'}${line}`;
    if (builder.collapsedRoot) node.nodeValue = builder.text.slice(start);
    return;
  }

  if (node.nodeType !== Node.ELEMENT_NODE) return;

  const element = node as Element;
  // Перенос разметкой поставлен намеренно — схлопывать его с границей блока нечего
  if (element.tagName === 'BR') {
    appendNodeLineBreak(element, builder);
    return;
  }

  const isBlock = BLOCK_LEVEL_TAGS.has(element.tagName);
  if (isBlock) appendBlockBoundary(builder);
  // Строка, оставленная пустой, своё место занимает — как на экране ({@link isBlankRichTextLine})
  if (isBlankRichTextLine(element)) {
    appendNodeLineBreak(element, builder);
    return;
  }
  // Копия списка детей: лишние переносы снимаются прямо по ходу чтения
  for (const child of Array.from(element.childNodes)) appendReadableNode(child, builder);
  if (isBlock) appendBlockBoundary(builder);
};

/** Пропуск внутри строки: всё пробельное, кроме переноса */
const INLINE_WHITESPACE_PATTERN = /[^\S\n]+/g;

/** Строки текста так, как их читает экран: пропуски подряд — один пробел, по краям строки — ничего */
const collapseLineWhitespace = (text: string): string =>
  text
    .split('\n')
    .map((line) => line.replace(INLINE_WHITESPACE_PATTERN, ' ').trim())
    .join('\n');

/**
 * Текст комментария для чтения: разметка снята, но границы абзацев и строк сохранены переносами.
 * Без них конец одного абзаца слипается с началом следующего в одно слово.
 *
 * Переносы, написанные в самом тексте, — такие же границы строк, как и разметка: текст, набранный
 * строками без единого тега, читается построчно и здесь, и на экране ({@link isRichTextLayoutWhitespace}).
 * Перенос на краю строки — отступ разметки, а не граница ({@link getRichTextLineRange}).
 *
 * Внутри строки текст тот же, что на экране: пропуски подряд читаются одним пробелом, а по краям
 * строки не читаются вовсе — иначе на месте убранного тега между словами остались бы пробелы
 * с обеих его сторон.
 */
export const richTextToReadableText = (html: string): string => {
  const builder: ReadableTextBuilder = { text: '' };
  for (const node of parseRichText(html).childNodes) appendReadableNode(node, builder);

  // Пустой абзац между текстом даёт двойной перенос — больше него отступ уже не читается
  return collapseLineWhitespace(builder.text)
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

/**
 * Текст без оформления для чтения — так, как его показывает экран (строками, `whitespace-pre-line`)
 * и печатает файл списка диалогов: строки и пустые строки между ними те же, что записаны, а внутри
 * строки пропуски подряд читаются одним пробелом и по краям строки не читаются. Знаки, похожие на
 * разметку (`x<y`), остаются как записаны — текст разметкой не читается.
 */
export const plainTextToReadableText = (text: string): string => collapseLineWhitespace(text).trim();

/** Перечисление и его пункт занимают строку маркером, даже пустые, — пустым краем текста они не бывают */
const LIST_TAGS = new Set(['UL', 'OL', 'LI']);

/** Узел, который на экране — лишь пустая строка или пропуск */
const isBlankRichTextNode = (node: Node): boolean => {
  if (node.nodeType !== Node.ELEMENT_NODE) return !node.textContent?.trim();
  const element = node as Element;
  return !LIST_TAGS.has(element.tagName) && !element.querySelector('li') && !element.textContent?.trim();
};

/** Снимает пустые строки и пропуски с одного края содержимого узла — вглубь до первого текста */
const trimRichTextEdge = (parent: Node, isStart: boolean): void => {
  const getEdge = () => (isStart ? parent.firstChild : parent.lastChild);
  let edge = getEdge();
  while (edge && isBlankRichTextNode(edge)) {
    edge.remove();
    edge = getEdge();
  }
  if (!edge) return;

  if (edge.nodeType === Node.TEXT_NODE) {
    const value = edge.nodeValue ?? '';
    edge.nodeValue = isStart ? value.trimStart() : value.trimEnd();
    return;
  }
  trimRichTextEdge(edge, isStart);
};

/**
 * Текст между тегами в порядке чтения; `null` — граница строки (перенос, начало или конец блока).
 * Пропуски разметки ({@link isRichTextLayoutWhitespace}) на экран не выходят и текстом не считаются.
 */
const collectRichTextLines = (parent: Node, items: (Text | null)[]): void => {
  for (const child of parent.childNodes) {
    if (child.nodeType === Node.TEXT_NODE) {
      if (!isRichTextLayoutWhitespace(child as Text)) items.push(child as Text);
      continue;
    }
    const isLineBreaking = isLineBreakingNeighbour(child);
    if (isLineBreaking) items.push(null);
    collectRichTextLines(child, items);
    if (isLineBreaking) items.push(null);
  }
};

/**
 * Пропуски в тексте между тегами — так, как их читает {@link richTextToReadableText}: подряд — один
 * пробел (неразрывные тоже), на стыке тегов — один на двоих, в начале и в конце строки — ни одного.
 * Неразрывный пробел не схлопывается с соседними, а типографика ставит его после предлога и в конце
 * строки, если строка кончилась предлогом и пробелом, — такие пробелы и снимаются заранее.
 */
const collapseRichTextWhitespace = (root: Node): void => {
  const items: (Text | null)[] = [];
  collectRichTextLines(root, items);

  // Перед текстом уже стоит пропуск или начинается строка — свой пропуск в его начале лишний
  let isAfterWhitespace = true;
  items.forEach((item, index) => {
    if (!item) {
      isAfterWhitespace = true;
      return;
    }
    const isLineEnd = !items[index + 1];
    const lines = (item.nodeValue ?? '').split('\n').map((line) => line.replace(INLINE_WHITESPACE_PATTERN, ' '));
    const value = lines
      .map((line, lineIndex) => {
        const isFirst = lineIndex === 0;
        const isLast = lineIndex === lines.length - 1;
        const start = !isFirst || isAfterWhitespace ? line.replace(/^ /, '') : line;
        return !isLast || isLineEnd ? start.replace(/ $/, '') : start;
      })
      .join('\n');

    item.nodeValue = value;
    if (value) isAfterWhitespace = /\s$/.test(value);
  });
};

/**
 * Пустые строки подряд — одна, чем бы они ни были заданы: переносами в тексте, переносами разметки
 * или пустыми абзацами. Разметка читается тем же порядком, что и {@link richTextToReadableText},
 * и переносы, которые тот схлопнул бы, из неё снимаются.
 */
const collapseRichTextLineBreaks = (root: Element): void => {
  const builder: ReadableTextBuilder = { text: '', collapsedRoot: root };
  for (const node of Array.from(root.childNodes)) appendReadableNode(node, builder);
};

/**
 * Оформленный текст в том виде, в каком его читает {@link richTextToReadableText}: пропуски подряд —
 * один пробел, по краям строк их нет, пустые строки подряд — одна, в начале и в конце их нет.
 * Показанный так, текст на экране совпадает с тем, что уходит в буфер обмена и в файл: иначе,
 * например, пустой абзац в начале значения стоял бы на экране строкой, а в буфер не попадал.
 */
export const normalizeRichTextSpacing = (html: string): string => {
  const root = parseRichText(html).firstElementChild;
  if (!root) return '';
  collapseRichTextWhitespace(root);
  collapseRichTextLineBreaks(root);
  trimRichTextEdge(root, true);
  trimRichTextEdge(root, false);
  return root.innerHTML;
};

/**
 * Теги, которыми оформляют текст: абзацы, строки, перечисления, начертания, ссылки.
 * Прочие теги (таблица, картинка) оформлением свободного текста не считаются.
 */
const TEXT_MARKUP_TAGS = new Set([
  'ADDRESS',
  'ARTICLE',
  'ASIDE',
  'BLOCKQUOTE',
  'DIV',
  'DD',
  'DL',
  'DT',
  'FIELDSET',
  'FIGCAPTION',
  'FIGURE',
  'FOOTER',
  'FORM',
  'H1',
  'H2',
  'H3',
  'H4',
  'H5',
  'H6',
  'HEADER',
  'HR',
  'LI',
  'MAIN',
  'NAV',
  'OL',
  'P',
  'PRE',
  'SECTION',
  'UL',
  'A',
  'B',
  'BR',
  'CODE',
  'DEL',
  'EM',
  'I',
  'INS',
  'MARK',
  'S',
  'SMALL',
  'SPAN',
  'STRIKE',
  'STRONG',
  'SUB',
  'SUP',
  'U',
]);

/** Атрибуты, с которыми пишется тег оформления */
const TEXT_MARKUP_ATTRIBUTES = new Set(['class', 'style', 'href', 'target', 'rel', 'title', 'id']);

/** Тег оформления, записанный как тег: посторонний атрибут выдаёт сравнение (`a<b and c>d`), а не полужирный */
const isTextMarkupElement = (element: Element): boolean =>
  TEXT_MARKUP_TAGS.has(element.tagName) &&
  Array.from(element.attributes).every(({ name }) => TEXT_MARKUP_ATTRIBUTES.has(name) || name.startsWith('data-'));

/**
 * Текст записан с оформлением, а не простым текстом.
 *
 * Свободный текст (значение атрибута) бывает и тем, и другим, и читаются они по-разному: у
 * оформленного разметка отрисовывается, простой берётся как записан — прочитанный разметкой,
 * `x<y` потерял бы хвост как недописанный тег. Поэтому оформленным текст делает только тег
 * оформления, а не любая угловая скобка. Признак тот же, по которому файл списка диалогов решает,
 * снимать ли с текста оформление, — иначе экран и файл разошлись бы на таком тексте.
 */
export const isRichTextMarkup = (value: string): boolean => {
  if (!value.includes('<')) return false;
  const body = new DOMParser().parseFromString(value, 'text/html').body;
  return Array.from(body.querySelectorAll('*')).some(isTextMarkupElement);
};

/** Знаки, которые в разметке значат не себя: текст с ними в разметку переносится экранированным */
const escapeRichTextChars = (text: string): string => {
  const holder = document.createElement('div');
  holder.textContent = text;
  return holder.innerHTML;
};

/**
 * Простой текст в разметку: границы строк, написанные переносами, становятся переносами разметки.
 *
 * Нужен тексту, пришедшему без разметки (составленному системой или сохранённому до того, как
 * его стали набирать с оформлением): редактор разметки принимает пропуски по правилам HTML и
 * схлопнул бы такие переносы в пробел — набранные строки пропали бы при первой же правке.
 */
export const plainTextToRichText = (text: string): string => escapeRichTextChars(text).replace(/\r?\n/g, '<br>');

/**
 * Простой текст в разметку абзацами: каждая строка — свой абзац, пустая строка — пустой абзац.
 *
 * Вариант {@link plainTextToRichText} для текста, за которым в той же разметке идут абзацы: строки,
 * переведённые переносами, остались бы вне абзаца и слиплись с первым абзацем после них.
 */
export const plainTextToRichParagraphs = (text: string): string =>
  text
    .split(/\r?\n/)
    .map((line) => `<p>${escapeRichTextChars(line) || '<br>'}</p>`)
    .join('');
